import { Box, Typography } from '@mui/material';
import { boundingBox, fitScale, polygonToLocal, polygonToPathD, placementToLocalRect } from './geometry2';
import { GREEN, CHARCOAL } from '../../../theme/tokens2';

// ─── LayoutThumb ────────────────────────────────────────────────────────────
// One tappable preview tile for a single LayoutOptionDto within a RoomUse's
// thumbnail strip. Renders the room's TRUE footprint (polygon + walls),
// scaled down, with the layout's furniture placements composited inside --
// the same rendering approach FloorplanCanvas uses for the full apartment,
// just applied to one room in isolation.
//
// Sizing: the thumbnail's pixel dimensions are the room's own bounding-box
// mm dimensions multiplied by `pxPerMm` -- a single factor shared by every
// room and every combination in this project (derived once, upstream, from
// the apartment's total envelope; see index.jsx). This means:
//   - every thumb for THIS room is the same size and shape (since they all
//     show the same room)
//   - a wider room gets a wider thumb than a narrower room gets, and a
//     bigger room gets a visibly bigger thumb than a smaller room -- by
//     design, since a bigger room represents a bigger decision
//   - thumbs are NOT forced into a uniform square box the way the old
//     fixed-88px-square version did, which is what caused furniture to
//     look cropped/distorted for non-square rooms
//
// A small min-px clamp is applied purely so a genuinely tiny room (e.g. a
// storage closet) doesn't shrink to illegibility -- this only ever raises
// a dimension that would otherwise round to something impractically small;
// it does not change the relative scaling between normally-sized rooms.
//
// Room-local frame: unlike FloorplanCanvas (which must place every room
// within one shared apartment-global frame, since it renders many rooms
// relative to each other), this component only ever shows ONE room at a
// time, in isolation -- so deriving a frame from that single room's own
// bounding box is correct here, not a re-zeroing bug.
//
// Props:
//   layoutOption     — a LayoutOptionDto
//   furniturePieces  — the global FurniturePieceDto[] catalog
//   footprintPolygon — the room's footprint polygon (apartment-global mm coords)
//   wallSegments     — the room's wall segments (apartment-global mm coords)
//   pxPerMm          — shared project-wide scale factor (see index.jsx)
//   label            — e.g. "Layout 2"
//   isActive         — boolean; this is the currently-selected layout for this room-use
//   onSelect         — fn() called when this thumb is clicked

const MIN_THUMB_DIMENSION = 28; // px -- legibility floor only, doesn't affect relative scaling
const LABEL_BAND_HEIGHT = 17; // px reserved below the floorplan for the text label

export default function LayoutThumb({
  layoutOption,
  furniturePieces,
  footprintPolygon,
  wallSegments,
  pxPerMm,
  label,
  isActive,
  onSelect,
}) {
  const [roomMinX, roomMaxX, roomMinY, roomMaxY] = boundingBox([footprintPolygon]);
  const roomWidthMm = roomMaxX - roomMinX;
  const roomHeightMm = roomMaxY - roomMinY;

  // Raw project-wide scale applied to this room's real dimensions, then
  // clamped up to MIN_THUMB_DIMENSION only if it would otherwise round to
  // something illegibly small -- the clamp never shrinks a thumb, only
  // rescues an already-tiny one.
  const rawWidth = roomWidthMm * pxPerMm;
  const rawHeight = roomHeightMm * pxPerMm;
  const clampScale = Math.max(1, MIN_THUMB_DIMENSION / rawWidth, MIN_THUMB_DIMENSION / rawHeight);
  const boxWidth = rawWidth * clampScale;
  const boxHeight = rawHeight * clampScale;

  return (
    <Box
      component="button"
      onClick={onSelect}
      aria-label={label}
      sx={{
        width: boxWidth,
        height: boxHeight + LABEL_BAND_HEIGHT,
        borderRadius: '3px',
        background: '#fff',
        border: `1.5px solid ${isActive ? GREEN : 'rgba(0,0,0,0.16)'}`,
        boxShadow: isActive ? `0 0 0 1px ${GREEN} inset` : 'none',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        transition: 'border-color 0.12s ease, transform 0.08s ease',
        '&:hover': { borderColor: isActive ? GREEN : 'rgba(0,0,0,0.32)' },
        '&:active': { transform: 'scale(0.97)' },
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: '4px' }}>
        <RoomThumbFloorplan
          layoutOption={layoutOption}
          furniturePieces={furniturePieces}
          footprintPolygon={footprintPolygon}
          wallSegments={wallSegments}
          roomMinX={roomMinX}
          roomMinY={roomMinY}
          roomWidthMm={roomWidthMm}
          roomHeightMm={roomHeightMm}
          boxWidth={boxWidth - 8}
          boxHeight={boxHeight - 8}
          isEmpty={!layoutOption.placements?.length}
        />
      </Box>
      <Typography
        sx={{
          fontSize: '9.5px', color: isActive ? GREEN : 'rgba(0,0,0,0.62)', fontWeight: isActive ? 600 : 500,
          textAlign: 'center', pb: '4px', flexShrink: 0,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function RoomThumbFloorplan({
  layoutOption,
  furniturePieces,
  footprintPolygon,
  wallSegments,
  roomMinX,
  roomMinY,
  roomWidthMm,
  roomHeightMm,
  boxWidth,
  boxHeight,
  isEmpty,
}) {
  const pieceByName = Object.fromEntries((furniturePieces || []).map((p) => [p.name, p]));
  const scale = fitScale(roomWidthMm, roomHeightMm, boxWidth, boxHeight);
  const viewW = roomWidthMm * scale;
  const viewH = roomHeightMm * scale;

  const localFootprint = polygonToLocal(footprintPolygon, roomMinX, roomMinY, roomHeightMm);
  const footprintPathD = polygonToPathD(localFootprint, scale);

  return (
    <Box component="svg" viewBox={`0 0 ${viewW} ${viewH}`} sx={{ width: viewW, height: viewH, maxWidth: '100%', maxHeight: '100%' }}>
      {/* Room footprint -- true polygon shape, dashed when this layout
          has no placements (shouldn't normally happen for a real
          furnished layout, but rendered defensively) so the room's real
          shape still reads, rather than a generic square glyph unrelated
          to the room. */}
      <path
        d={footprintPathD}
        fill="#fff"
        stroke={isEmpty ? 'rgba(0,0,0,0.40)' : 'rgba(0,0,0,0.55)'}
        strokeWidth={isEmpty ? 1.2 : 0.75}
        strokeDasharray={isEmpty ? '4 3' : undefined}
        opacity={isEmpty ? 0.75 : 1}
      />

      {!isEmpty &&
        wallSegments.map((segment, i) => {
          const localSegment = polygonToLocal(segment, roomMinX, roomMinY, roomHeightMm);
          const [[x1, y1], [x2, y2]] = localSegment;
          return (
            <line
              key={i}
              x1={x1 * scale} y1={y1 * scale}
              x2={x2 * scale} y2={y2 * scale}
              stroke={CHARCOAL}
              strokeWidth={1}
              strokeLinecap="round"
            />
          );
        })}

      {!isEmpty &&
        layoutOption.placements.map((placement) => {
          const piece = pieceByName[placement.furniturePieceName];
          if (!piece) return null; // unknown stencil name -- skip rather than render a broken image

          // Room-local frame is correct here -- see file header comment.
          const rect = placementToLocalRect(piece, placement, roomMinX, roomMinY, roomHeightMm, scale);

          return (
            <image
              key={placement.id}
              href={piece.imageUrl}
              x={rect.x}
              y={rect.y}
              width={rect.width}
              height={rect.height}
              transform={rect.transform}
              preserveAspectRatio="none"
            />
          );
        })}
    </Box>
  );
}